import { CLAIMS_FOOTER_TYPES } from '@/views/screens/Claims/constants';
import type { LossAndDamageSearchFilters, OverChargeSearchFilters } from '@/views/screens/Claims/types';

export const OVERCHARGE_NOTES_TYPES_OPTIONS = [
  { label: 'External', value: 'x' },
  { label: 'In-House', value: 'i' },
];

export const CLAIMS_SHIPPER_CONSIGNEE_FETCH_ERROR_TYPE = {
  CLEAR: 'CLEAR',
  EMPTY: 'EMPTY',
  ERROR: 'ERROR',
  SUCCESS: 'SUCCESS',
};

export const LOSS_AND_DAMAGE_CLAIMS_FILTERS_INITIAL_DATA: LossAndDamageSearchFilters = {
  branchKey: undefined,
  carrierKey: undefined,
  claimNo: undefined,
  claimProcessor: undefined,
  claimStatus: ['O', 'P', 'H', 'C'],
  claimType: undefined,
  consignee: undefined,
  daysOld: undefined,
  divisionKey: undefined,
  endDate: undefined,
  FooterStatus: CLAIMS_FOOTER_TYPES.ALL,
  freightBillNo: undefined,
  invoiceNo: undefined,
  pageNo: 1,
  pageSize: 10,
  regionKey: undefined,
  sortColumn: 'CreatedOn',
  sortDirection: 'DESC',
  startDate: undefined,
};

export const LAD_CLAIM_EXPORT_FIELDS = [
  { label: 'Claim Number', name: 'claimNumber' },
  { label: 'Freight Bill / Pro Number', name: 'claimProNo' },
  { label: 'Invoice Number', name: 'invoiceNo' },
  { label: 'Transportation Provider', name: 'carrierName' },
  { label: 'Consignee', name: 'consignee' },
  { label: 'Amount Filed', name: 'claimTotal' },
  { label: 'Amount Paid', name: 'claimPaidAmount' },
  { label: 'Difference Amount', name: 'difference' },
  { label: 'Claim Type', name: 'claimType' },
  { label: 'Status', name: 'claimStatus' },
  { label: 'Filed Date', name: 'dateFiled' },
  { label: 'Closed Date', name: 'claimCloseDate' },
  { label: 'Created On', name: 'createdOn' },
  { label: 'Created By', name: 'createdByUser' },
  { label: 'Modified On', name: 'modifiedOn' },
  { label: 'Modified By', name: 'modifiedByUser' },
];

export const CREATE_LD_CLAIM_FORM_IDS = {
  claimsDetailsForm: '1',
  claimsFinancialsForm: '3',
};

export const CLAIMS_TOTAL_COUNTS_TYPES = {
  [CLAIMS_FOOTER_TYPES.ALL]: 'totalClaim',
  [CLAIMS_FOOTER_TYPES.OPEN]: 'openClaim',
  [CLAIMS_FOOTER_TYPES.CLOSED]: 'closedClaim',
  [CLAIMS_FOOTER_TYPES.HOLD]: 'holdClaim',
  ALL_FOR_OC_CLAIM: 'totalRecords',
};

export const OVERCHARGE_CLAIMS_FILTERS_INITIAL_DATA: OverChargeSearchFilters = {
  byAge: false,
  carrierKey: undefined,
  daysOld: 0,
  EndDate: undefined,
  FooterStatus: CLAIMS_FOOTER_TYPES.ALL,
  fbocKey: '',
  freightBillNo: '',
  invoiceNo: '',
  packageNo: '',
  pageNo: 1,
  pageSize: 10,
  StartDate: undefined,
  StatusCodes: [],
  sortColumn: 'FBOCKey',
  sortDirection: 'DESC',
};

export const DEFAULT_LOSS_AND_DAMAGE_CLAIMS_TABLE_VIEW_COLUMNS = [
  {
    columnName: 'Claim Status',
    columnValue: 'claimStatus',
  },
  {
    columnName: 'Claim Type',
    columnValue: 'claimType',
  },
  {
    columnName: 'Freight Bill / Pro Number',
    columnValue: 'claimProNo',
  },
  {
    columnName: 'Invoice Number',
    columnValue: 'invoiceNo',
  },
  {
    columnName: 'Transportation Provider',
    columnValue: 'carrierName',
  },
  {
    columnName: 'Consignee',
    columnValue: 'consignee',
  },
  {
    columnName: 'Amount Filed',
    columnValue: 'claimTotal',
  },
  {
    columnName: 'Amount Paid',
    columnValue: 'claimPaidAmount',
  },
  {
    columnName: 'Difference Amount',
    columnValue: 'difference',
  },
];

export const DEFAULT_OVERCHARGE_CLAIMS_TABLE_VIEW_COLUMNS = [
  {
    columnName: 'Claim Status',
    columnValue: 'claimStatusDescription',
  },
  {
    columnName: 'Overcharge Amount',
    columnValue: 'overchargeAmount',
  },
  {
    columnName: 'Recovery Amount',
    columnValue: 'recoveryAmount',
  },
  {
    columnName: 'Package Number',
    columnValue: 'packageNumber',
  },
  {
    columnName: 'Invoice Number',
    columnValue: 'invoiceNumber',
  },
  {
    columnName: 'Claim Type',
    columnValue: 'claimTypeDesc',
  },
  {
    columnName: 'Reason',
    columnValue: 'reasonCodeDesc',
  },
  {
    columnName: 'Submitted Date',
    columnValue: 'dateSubmitted',
  },
  {
    columnName: 'Closed Date',
    columnValue: 'closedDate',
  },
];

export const INTEGER_NUMBER_VALIDATION_REGEX = /^[1-9][0-9]*$/g;
export const FLOAT_NUMBER_VALIDATION_REGEX = /^(?=.*[1-9])[0-9]*[.,]?[0-9]{1,}$/g;

export const LD_CLAIM_DUPLICATE_STATUS = {
  BOTH: 3,
  INVOICE_NO: 2,
  NEW: 0,
  PRO_NUMBER: 1,
} as const;

export const LD_CLAIM_DUPLICATE_STATUS_MESSAGE = {
  [LD_CLAIM_DUPLICATE_STATUS.PRO_NUMBER]:
    'A Claim exists with the Freight Bill / Pro Number you have entered. Do you want to save anyway?',
  [LD_CLAIM_DUPLICATE_STATUS.INVOICE_NO]:
    'A Claim exists with the Invoice Number you have entered. Do you want to save anyway?',
  [LD_CLAIM_DUPLICATE_STATUS.BOTH]:
    'A Claim exists with both the Freight Bil and Invoice Number you have entered. Do you want to save anyway?',
};

export const LOSS_AND_DAMAGE_CREATE_CLAIM_DEFAULT_VALUE = {
  FLIES: [
    {
      claimKey: 0,
      description: undefined,
      documentType: undefined,
      editMode: true,
      fileComments: undefined,
      fileDesc: undefined,
      fileName: undefined,
      isInitial: true,
      isNew: true,
      originalFileName: undefined,
      sequence: 1,
    },
  ],
  ITEMS: [
    {
      claimKey: 0,
      dirtyFlag: 'i',
      editMode: true,
      isInitial: true,
      isNew: true,
      itemSeq: 1,
      smAmount: undefined,
    },
  ],
  NOTES: [
    {
      commentText: undefined,
      commentType: undefined,
      dirtyFlag: 'i',
      editMode: true,
      isInitial: true,
      isNew: true,
      oldCommentType: null,
      sequenceNo: 1,
    },
  ],
  SETTLEMENT: [
    {
      accountingCode: undefined,
      amount: undefined,
      claimKey: 0,
      dirtyFlag: 'i',
      editMode: true,
      isInitial: true,
      isNew: true,
      settlementSeq: 1,
    },
  ],
};

export const CREATE_OVER_CHARGE_CLAIM_DEFAULT_VALUE = {
  ITEMS: [
    {
      billedAmount: 0.0,
      correctCharges: 0.0,
      dirtyFlag: 'i',
      editMode: true,
      fbOcKey: 0,
      invoiceNumber: undefined,
      isInitial: true,
      isNew: true,
      itemID: 0,
      overchargeAmount: 0,
      packageNumber: undefined,
      paidAmount: 0.0,
      proNo: undefined,
      recoveryAmount: 0.0,
      shipDate: undefined,
      weight: 0,
    },
  ],
  NOTES: [
    {
      commentText: undefined,
      commentType: undefined,
      dirtyFlag: 'i',
      editMode: true,
      isInitial: true,
      isNew: true,
      oldCommentType: null,
      sequenceNo: 1,
    },
  ],
  SETTLEMENT: [
    {
      accountingCode: undefined,
      amount: 0.0,
      dirtyFlag: 'i',
      editMode: true,
      fbocKey: 0,
      isInitial: true,
      isNew: true,
      settlementSeq: 1,
    },
  ],
};
